// =====================================================================================
// INVRT Radar - web console - organisation switcher
//
// A user may belong to several organisations; `memberships` is the only place that says
// which. The chosen id becomes the X-Org-Id header on every API call and the server
// VERIFIES it against memberships on each request - this control declares intent, it does
// not grant access.
//
// The tenant scope guard (PRD_RESELLER_RESET_2026-10-08, R7, the owner's ruling: an
// organisation admin must NEVER see other organisations): the switcher renders ONLY for a
// platform super-admin. Everyone else is pinned - a multi-membership non-super-admin to
// their first membership in stable org_id order (SessionProvider discards any remembered
// choice) with an honest "Signed in to <org>" notice, and a single-membership
// non-super-admin to nothing at all. The server refuses a foreign X-Org-Id regardless.
// =====================================================================================

import { Building2 } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { Badge } from "./ui/badge";

export function OrgSwitcher() {
  const { memberships, membershipsLoading, orgId, setOrgId, activeMembership, isSuperAdmin } =
    useSession();

  if (membershipsLoading) {
    return <span className="text-sm text-ink-muted">Loading organisations…</span>;
  }

  if (memberships.length === 0) {
    return (
      <span className="text-sm text-ink-muted">
        Your account is not a member of any organisation yet.
      </span>
    );
  }

  if (!isSuperAdmin) {
    // A single-membership member: no switcher at all.
    if (memberships.length === 1) return null;
    // Several memberships without the platform role: pinned to the first (stable order);
    // the session has already discarded any remembered choice. Name only the pinned org.
    const pinned = memberships[0];
    return (
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-ink-muted" aria-hidden="true" />
        <span className="text-sm font-medium text-ink">
          Signed in to {pinned?.organizations?.name ?? pinned?.org_id}
        </span>
        {activeMembership?.is_org_admin ? <Badge tone="info">Administrator</Badge> : null}
      </div>
    );
  }

  if (memberships.length === 1) {
    const only = memberships[0];
    return (
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-ink-muted" aria-hidden="true" />
        <span className="text-sm font-medium text-ink">
          {only?.organizations?.name ?? only?.org_id}
        </span>
        {activeMembership?.is_org_admin ? <Badge tone="info">Administrator</Badge> : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="radar-org-switcher" className="sr-only">
        Organisation
      </label>
      <Building2 className="h-4 w-4 text-ink-muted" aria-hidden="true" />
      <select
        id="radar-org-switcher"
        value={orgId ?? ""}
        onChange={(event) => setOrgId(event.target.value)}
        className="h-9 rounded-lg border border-line bg-surface px-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {memberships.map((membership) => (
          <option key={membership.org_id} value={membership.org_id}>
            {membership.organizations?.name ?? membership.org_id}
          </option>
        ))}
      </select>
      {activeMembership?.is_org_admin ? <Badge tone="info">Administrator</Badge> : null}
    </div>
  );
}
